(function () {
  'use strict';

  if (window.__digitalFeeModuleInitialized) {
    return;
  }
  window.__digitalFeeModuleInitialized = true;

const STORE_ENABLED_SESSION_KEY_PREFIX = 'digitalFeeStoreEnabled:';

/** @type {boolean | null} */
let digitalFeeStoreEnabledCache = null;
/** @type {Promise<boolean> | null} */
let digitalFeeStoreEnabledPromise = null;

function getStoreEnabledSessionKey() {
  const storeNumber = window.digital_product_config?.store_number;
  return `${STORE_ENABLED_SESSION_KEY_PREFIX}${storeNumber || 'unknown'}`;
}

function readStoreEnabledSessionCache() {
  try {
    const raw = sessionStorage.getItem(getStoreEnabledSessionKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.enabled !== 'boolean') return null;
    return parsed.enabled;
  } catch {
    return null;
  }
}

function writeStoreEnabledSessionCache(enabled) {
  try {
    sessionStorage.setItem(
      getStoreEnabledSessionKey(),
      JSON.stringify({ enabled: Boolean(enabled), at: Date.now() })
    );
  } catch {
    /* private mode / quota */
  }
}

function rememberStoreEnabledFromPayload(data) {
  if (
    !Array.isArray(data) ||
    data.length === 0 ||
    !data.every((entry) => typeof entry?.storeEnabled === 'boolean')
  ) {
    return;
  }
  const enabled = data.some((entry) => entry.storeEnabled === true);
  digitalFeeStoreEnabledCache = enabled;
  writeStoreEnabledSessionCache(enabled);
}

/**
 * Store-level downloadprovidercode=* check. Failures are not a confirmed store-off.
 * @returns {Promise<boolean>}
 */
function isDigitalDeliveryNoticeStoreEnabled() {
  if (digitalFeeStoreEnabledCache !== null) {
    return Promise.resolve(digitalFeeStoreEnabledCache);
  }
  if (digitalFeeStoreEnabledPromise) {
    return digitalFeeStoreEnabledPromise;
  }

  const sessionCached = readStoreEnabledSessionCache();
  if (sessionCached !== null) {
    digitalFeeStoreEnabledCache = sessionCached;
    return Promise.resolve(sessionCached);
  }

  const config = window.digital_product_config;
  if (!config?.api_url || !config?.store_number) {
    digitalFeeStoreEnabledCache = false;
    return Promise.resolve(false);
  }

  const requestUrl = `${config.api_url}?storeNumber=${config.store_number}&downloadprovidercode=*`;
  digitalFeeStoreEnabledPromise = fetch(requestUrl)
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const enabled =
        Array.isArray(data) &&
        data.some((entry) => entry?.storeEnabled === true);
      digitalFeeStoreEnabledCache = enabled;
      writeStoreEnabledSessionCache(enabled);
      return enabled;
    })
    .catch((e) => {
      console.error('[digital-fee] Store enabled check failed:', e);
      digitalFeeStoreEnabledCache = false;
      return false;
    })
    .finally(() => {
      digitalFeeStoreEnabledPromise = null;
    });

  return digitalFeeStoreEnabledPromise;
}

/**
 * ATC / cart: publisher isApplied is the source of truth.
 * storeEnabled=false must not skip this — TCU returns isApplied true with storeEnabled false.
 * @param {string} publisherCode
 * @returns {Promise<boolean>}
 */
async function shouldApplyDigitalFeeForPublisher(publisherCode) {
  const code = String(publisherCode || '').trim();
  if (!code) return false;

  try {
    const results = await queryDigitalDeliveryFeesApi(code);
    return Array.isArray(results) && results.some((entry) => entry?.isApplied === true);
  } catch (e) {
    console.error('[digital-fee] Publisher fee check failed:', e);
    return false;
  }
}

window.isDigitalDeliveryNoticeStoreEnabled = isDigitalDeliveryNoticeStoreEnabled;
window.shouldApplyDigitalFeeForPublisher = shouldApplyDigitalFeeForPublisher;
window.queryDigitalDeliveryFeesApi = queryDigitalDeliveryFeesApi;
window.buildDigitalFeeCartLine = buildDigitalFeeCartLine;
window.addCartItems = addCartItems;

/**
 * Shopify /cart/add.js often returns HTTP 200 with { status: 422 }.
 * @param {Response} response
 * @returns {Promise<any>}
 */
async function parseCartAddResponse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || Number(payload?.status) >= 400) {
    throw new Error(
      payload?.description || payload?.message || `Failed to add items to cart (HTTP ${response.status})`
    );
  }
  return payload;
}

/**
 * @param {number|string} quantity
 * @returns {Promise<{id: number, quantity: number, properties: Record<string, string>} | null>}
 */
async function buildDigitalFeeCartLine(quantity) {
  const feeProductHandle = window.digital_product_config?.product_handle ?? '';
  if (!feeProductHandle) return null;

  try {
    const productResponse = await fetch(`/products/${feeProductHandle}.js`);
    if (!productResponse.ok) return null;

    const feeProduct = await productResponse.json();
    const feeVariantId = feeProduct.variants?.[0]?.id;
    if (!feeVariantId) return null;

    let metafields = [];
    if (typeof window.fetchMetafieldsOnce === 'function') {
      metafields = await window.fetchMetafieldsOnce(feeVariantId);
    }

    const getMetafieldValue = (key) => {
      const metafield = (metafields || []).find((m) => m.key === key);
      return metafield != null ? String(metafield.value ?? '') : '';
    };

    const feeSubClass = getMetafieldValue('sub_class');
    window.appendSubClassCartAudit?.(feeSubClass, 'digital-fee.js', 0);

    return {
      id: Number(feeVariantId),
      quantity: Number(quantity) || 1,
      properties: {
        _digital_delivery_fee: 'true',
        _is_digital: 'true',
        _fee_type: 'online_store',
        _department: getMetafieldValue('department'),
        _sub_department: getMetafieldValue('sub_department'),
        _class: getMetafieldValue('class'),
        _sub_class: feeSubClass,
      },
    };
  } catch (error) {
    console.error('[digital-fee] Failed to build fee line:', error);
    return null;
  }
}

/**
 * One /cart/add.js for product lines + digital fee. Does not add rental collateral.
 * @param {Array<{id: number|string, quantity: number|string, properties?: Record<string, string>}>} items
 * @returns {Promise<any>}
 */
async function addCartItems(items) {
  const payloadItems = (items || []).filter((item) => item?.id != null);
  if (payloadItems.length === 0) {
    throw new Error('No items to add to cart');
  }

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: payloadItems }),
  });
  return parseCartAddResponse(response);
}

/**
 * Checks if a digital fee should be applied for given publisher codes
 * @param {string | string[]} publisherCodes - Publisher code(s) to check
 * @returns {Promise<Array<{code: string, isApplied: boolean, storeEnabled?: boolean}>>}
 */
function queryDigitalDeliveryFeesApi(publisherCodes) {
  if (
    window.digital_product_config == undefined ||
    window.digital_product_config?.api_url == undefined ||
    window.digital_product_config?.store_number == undefined
  ) {
    throw Error("Digital fee config does not exist");
  }

  const api_url = window.digital_product_config.api_url;
  const store_number = window.digital_product_config.store_number;

  let uniqueCodes;
  if (typeof publisherCodes === "string") {
    uniqueCodes = [
      ...new Set(
        publisherCodes
          .split("|")
          .map((s) => s.trim())
          .filter(Boolean)
      ),
    ];
  } else {
    uniqueCodes = [
      ...new Set(
        publisherCodes.map((c) => String(c).trim()).filter(Boolean)
      ),
    ];
  }
  const queryValue = uniqueCodes.join("|");

  if (queryValue !== "") {
    const requestUrl = `${api_url}?storeNumber=${store_number}&downloadprovidercode=${queryValue}`;
    return fetch(requestUrl)
      .then((res) => res.json())
      .then((data) => {
        rememberStoreEnabledFromPayload(data);
        const mapped = data.map((entry) => {
          return {
            code: entry.code,
            isApplied: entry.isApplied ?? false,
            storeEnabled: entry.storeEnabled ?? false,
          };
        });
        return mapped;
      })
      .catch((e) => {
        console.error("[digital-fee] API request failed:", e);
        return [];
      });
  } else {
    return Promise.resolve([]);
  }
}

/** All `.purchase-checkbox` nodes whose `data-variant-id` matches (safe vs. CSS selector quirks). */
function cmrpFindPurchaseCheckboxesByVariantId(variantId) {
  const want = String(variantId || "");
  if (!want) return [];
  return Array.from(document.querySelectorAll("input.purchase-checkbox")).filter(
    (el) => el.getAttribute("data-variant-id") === want
  );
}

/**
 * Clear digital fee variant notices and remove digital fee tooltips on digital rows only.
 * Print used-material tooltips use `.notice-tooltip` without `data-variant-id`.
 */
function suppressDigitalFeeNoticesOnDigitalVariants() {
  const collapsesToSync = new Set();

  document
    .querySelectorAll('input.purchase-checkbox[data-is-digital="true"]')
    .forEach((input) => {
      const row = input.closest(".purchase-type, .accordion-variant-option");
      if (!row) return;

      row.removeAttribute("data-variant-notice");
      row.querySelectorAll(".notice-tooltip[data-variant-id]").forEach((el) => {
        el.remove();
      });

      const collapse = row.closest(".collapse-content");
      if (collapse) collapsesToSync.add(collapse);
    });

  if (typeof window.syncCmrpVariantNoticeInCollapse === "function") {
    collapsesToSync.forEach((c) =>
      window.syncCmrpVariantNoticeInCollapse(c)
    );
  }
}

// Digital delivery fee API: set `data-variant-notice` on matching digital rows (CMRP inline notice).
// Multiple variants can share one publisher code (e.g. unified-view merged digitals) — map code → many variant ids.
function pageHasDigitalPurchaseUi() {
  return Boolean(
    document.querySelector('input.purchase-checkbox[data-is-digital="true"]') ||
      document.querySelector('.textbook-variant-picker') ||
      document.body.classList.contains('course-materials-page')
  );
}

async function applyDigitalDeliveryFeeVariantNotices() {
  if (!pageHasDigitalPurchaseUi()) {
    return;
  }

  const checkboxContainers = document.querySelectorAll(
    ".purchase-checkbox-container"
  );

  if (checkboxContainers.length === 0) {
    return;
  }

  /** @type {Map<string, Set<string>>} */
  const publisherToVariantIds = new Map();
  checkboxContainers.forEach((container) => {
    const input = container.querySelector("input.purchase-checkbox");

    if (!input) return;

    const isDigital = input.dataset.isDigital === "true";
    const publisherCode = input.dataset.publisherCode;
    const variantId = input.getAttribute("data-variant-id");

    if (
      isDigital &&
      publisherCode &&
      publisherCode !== "null" &&
      publisherCode.trim() !== ""
    ) {
      const code = publisherCode.trim();
      if (!publisherToVariantIds.has(code)) {
        publisherToVariantIds.set(code, new Set());
      }
      if (variantId) {
        publisherToVariantIds.get(code).add(variantId);
      }
    }
  });

  const publisherCodesToCheck = Array.from(publisherToVariantIds.keys());
  if (publisherCodesToCheck.length === 0) {
    return;
  }

  return queryDigitalDeliveryFeesApi(publisherCodesToCheck).then((results) => {
    if (!Array.isArray(results)) {
      return;
    }

    const noticeText = window.digital_product_config?.notice_text;
    const collapsesToSync = new Set();

    if (noticeText) {
      results.forEach((result) => {
        const isApplied = result.isApplied ?? false;
        if (!isApplied) return;

        const variantIds = publisherToVariantIds.get(result.code);
        if (!variantIds || variantIds.size === 0) return;

        variantIds.forEach((vid) => {
          cmrpFindPurchaseCheckboxesByVariantId(vid).forEach((checkbox) => {
            const row = checkbox.closest(".purchase-type");
            if (row) {
              row.setAttribute("data-variant-notice", noticeText);
              const collapse = row.closest(".collapse-content");
              if (collapse) collapsesToSync.add(collapse);
            }
          });
        });
      });
    }

    const appliedCodes = new Set();
    results.forEach((result) => {
      if (result.isApplied ?? false) {
        appliedCodes.add(result.code);
      }
    });

    checkboxContainers.forEach((container) => {
      const input = container.querySelector("input.purchase-checkbox");
      if (!input) return;

      const isDigital = input.dataset.isDigital === "true";
      const publisherCode = input.dataset.publisherCode;

      if (
        !isDigital ||
        !publisherCode ||
        publisherCode === "null" ||
        !publisherCode.trim() ||
        !appliedCodes.has(publisherCode.trim())
      ) {
        return;
      }

      const row = container.closest(".purchase-type, .accordion-variant-option");
      const tooltip = row?.querySelector(".notice-tooltip[data-variant-id]");
      if (tooltip) {
        tooltip.classList.remove("hidden");
      }
    });

    if (
      collapsesToSync.size > 0 &&
      typeof window.syncCmrpVariantNoticeInCollapse === "function"
    ) {
      collapsesToSync.forEach((c) =>
        window.syncCmrpVariantNoticeInCollapse(c)
      );
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", applyDigitalDeliveryFeeVariantNotices);
} else {
  applyDigitalDeliveryFeeVariantNotices();
}

window.applyDigitalDeliveryFeeVariantNotices =
  applyDigitalDeliveryFeeVariantNotices;

document.addEventListener("product-display:updated", () => {
  applyDigitalDeliveryFeeVariantNotices();
});

})();
